/**
 * Custom hooks for the Servicing Pipeline
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  ServicingLoan,
  ServicingPayment,
  CompletedServicingPayment,
  ServicingSummaryMetrics,
  ServicingLoanStatus,
  RecordPaymentInput,
  ReverseServicingPaymentInput,
  Form1098Data,
  PaymentReceivedPayload
} from '@/types/servicing';
import {
  listServicingLoans,
  listCompletedServicingPayments,
  getServicingLoan,
  getPaymentsForLoan,
  getSummaryMetrics,
  recordPayment,
  reversePayment,
  deleteServicingLoan,
  getForm1098Data,
  sendPaymentReceivedConfirmation,
  getCurrentUserEncompassUserId,
  listOwnServicingLoans,
  getOwnServicingLoan,
  getOwnPaymentsForLoan,
  getOwnServicingSummaryMetrics
} from '@/services/servicingService';

interface UseCompletedServicingPaymentsReturn {
  payments: CompletedServicingPayment[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useCompletedServicingPayments(
  organizationId: string | undefined,
  enabled: boolean
): UseCompletedServicingPaymentsReturn {
  const [payments, setPayments] = useState<CompletedServicingPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!organizationId || !enabled) {
      setPayments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setPayments(await listCompletedServicingPayments(organizationId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load completed servicing payments');
    } finally {
      setLoading(false);
    }
  }, [enabled, organizationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { payments, loading, error, refresh };
}

// ============================================================================
// useServicingLoans — List + metrics for the dashboard
// ============================================================================

interface UseServicingLoansReturn {
  loans: ServicingLoan[];
  metrics: ServicingSummaryMetrics | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  statusFilter: ServicingLoanStatus | undefined;
  setStatusFilter: (status: ServicingLoanStatus | undefined) => void;
}

export interface ServicingStatusCounts {
  all: number;
  active: number;
  completed: number;
  transferred: number;
}

const EMPTY_STATUS_COUNTS: ServicingStatusCounts = {
  all: 0,
  active: 0,
  completed: 0,
  transferred: 0
};

function countLoanStatuses(loans: ServicingLoan[]): ServicingStatusCounts {
  return loans.reduce<ServicingStatusCounts>(
    (counts, loan) => {
      counts.all += 1;
      counts[loan.status] += 1;
      return counts;
    },
    { ...EMPTY_STATUS_COUNTS }
  );
}

export function useServicingLoans(organizationId: string | undefined): UseServicingLoansReturn {
  const [loans, setLoans] = useState<ServicingLoan[]>([]);
  const [metrics, setMetrics] = useState<ServicingSummaryMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ServicingLoanStatus | undefined>('active');

  const refresh = useCallback(async () => {
    if (!organizationId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Include virtual rows (from encompass_loans) for active and all filters
      const includeUpcoming = statusFilter === 'active' || !statusFilter;

      const [loanData, { metrics: metricsData, upcomingLoans: upcomingData }] = await Promise.all([
        listServicingLoans(organizationId, statusFilter),
        getSummaryMetrics(organizationId)
      ]);

      // Virtual rows first (not-purchased, most urgent), then servicing loans
      const combined = includeUpcoming
        ? [...upcomingData, ...loanData]
        : loanData;

      setLoans(combined);
      setMetrics(metricsData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load servicing data');
    } finally {
      setLoading(false);
    }
  }, [organizationId, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { loans, metrics, loading, error, refresh, statusFilter, setStatusFilter };
}

interface UseLoanOfficerServicingLoansReturn extends UseServicingLoansReturn {
  profileLinked: boolean | null;
  statusCounts: ServicingStatusCounts;
}

export function useLoanOfficerServicingLoans(enabled = true): UseLoanOfficerServicingLoansReturn {
  const [allOwnedLoans, setAllOwnedLoans] = useState<ServicingLoan[]>([]);
  const [metrics, setMetrics] = useState<ServicingSummaryMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ServicingLoanStatus | undefined>(undefined);
  const [profileLinked, setProfileLinked] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setAllOwnedLoans([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const encompassUserId = await getCurrentUserEncompassUserId();
      const linked = !!encompassUserId;
      setProfileLinked(linked);
      if (!linked) {
        setAllOwnedLoans([]);
        setMetrics({
          totalActiveLoans: 0,
          totalExpectedPayments: 0,
          totalCollected: 0,
          totalOutstanding: 0,
          netServicingIncome: 0,
          overduePaymentsCount: 0
        });
        return;
      }

      const loanData = await listOwnServicingLoans(undefined);
      const metricsData = await getOwnServicingSummaryMetrics(loanData);
      setAllOwnedLoans(loanData);
      setMetrics(metricsData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load servicing data');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loans = useMemo(
    () => statusFilter
      ? allOwnedLoans.filter((loan) => loan.status === statusFilter)
      : allOwnedLoans,
    [allOwnedLoans, statusFilter]
  );

  const statusCounts = useMemo(
    () => countLoanStatuses(allOwnedLoans),
    [allOwnedLoans]
  );

  return {
    loans,
    metrics,
    loading,
    error,
    refresh,
    statusFilter,
    setStatusFilter,
    profileLinked,
    statusCounts
  };
}

// ============================================================================
// useServicingLoanDetail — Single loan + payments
// ============================================================================

interface UseServicingLoanDetailReturn {
  loan: ServicingLoan | null;
  payments: ServicingPayment[];
  form1098Data: Form1098Data | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  handleRecordPayment: (input: RecordPaymentInput) => Promise<{ success: boolean; error?: string; nextPaymentDueDate?: string; nextPaymentAmount?: number }>;
  handleReversePayment: (input: ReverseServicingPaymentInput) => Promise<{ success: boolean; error?: string }>;
  handleDelete: () => Promise<boolean>;
}

export function useServicingLoanDetail(
  servicingLoanId: string | null
): UseServicingLoanDetailReturn {
  const [loan, setLoan] = useState<ServicingLoan | null>(null);
  const [payments, setPayments] = useState<ServicingPayment[]>([]);
  const [form1098Data, setForm1098Data] = useState<Form1098Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!servicingLoanId) {
      setLoan(null);
      setPayments([]);
      setForm1098Data(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [loanData, paymentData] = await Promise.all([
        getServicingLoan(servicingLoanId),
        getPaymentsForLoan(servicingLoanId)
      ]);

      setLoan(loanData);
      setPayments(paymentData);

      // Fetch Form 1098 data from encompass_loans (non-blocking)
      if (loanData?.encompass_loan_id) {
        getForm1098Data(loanData.encompass_loan_id, loanData.organization_id).then(setForm1098Data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load loan detail');
    } finally {
      setLoading(false);
    }
  }, [servicingLoanId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRecordPayment = useCallback(
    async (input: RecordPaymentInput) => {
      // Build email payload from current state BEFORE mutation
      let emailPayload: PaymentReceivedPayload | null = null;
      if (loan?.encompass_loan_id && loan?.loan_number) {
        const recordedPayment = payments.find(p => p.payment_number === input.payment_number);
        const nextPayment = payments.find(
          p => p.payment_number > input.payment_number && p.status !== 'received' && p.status !== 'waived'
        );
        emailPayload = {
          encompassLoanId: loan.encompass_loan_id,
          servicingLoanId: input.servicing_loan_id,
          loanNumber: loan.loan_number,
          paymentNumber: input.payment_number,
          amountReceived: input.total_amount_received,
          principalReceived: input.principal_received,
          interestReceived: input.interest_received,
          escrowReceived: input.escrow_received,
          dueDate: recordedPayment?.due_date || '',
          dateReceived: input.payment_received_date,
          paymentMethod: input.payment_method,
          referenceNumber: input.reference_number,
          nextPaymentDueDate: nextPayment?.due_date,
          nextPaymentAmount: nextPayment ? Number(nextPayment.total_amount_due) : undefined
        };
      }

      const result = await recordPayment(input);
      if (result.success) {
        // Patch next-payment info when roll-forward appended a new payment
        if (emailPayload && !emailPayload.nextPaymentDueDate && result.nextPaymentDueDate) {
          emailPayload.nextPaymentDueDate = result.nextPaymentDueDate;
          emailPayload.nextPaymentAmount = result.nextPaymentAmount;
        }
        await refresh();
        if (emailPayload) {
          sendPaymentReceivedConfirmation(emailPayload).catch(() => {});
        }
      }
      return result;
    },
    [refresh, loan, payments]
  );

  const handleDelete = useCallback(async () => {
    if (!servicingLoanId) {
      return false;
    }
    const result = await deleteServicingLoan(servicingLoanId);
    if (result) {
      setLoan(null);
      setPayments([]);
    }
    return result;
  }, [servicingLoanId]);

  const handleReversePayment = useCallback(async (input: ReverseServicingPaymentInput) => {
    const result = await reversePayment(input);
    if (result.success) {
      await refresh();
    }
    return result;
  }, [refresh]);

  return {
    loan,
    payments,
    form1098Data,
    loading,
    error,
    refresh,
    handleRecordPayment,
    handleReversePayment,
    handleDelete
  };
}

export function useLoanOfficerServicingLoanDetail(
  servicingLoanId: string | null
): UseServicingLoanDetailReturn {
  const [loan, setLoan] = useState<ServicingLoan | null>(null);
  const [payments, setPayments] = useState<ServicingPayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!servicingLoanId) {
      setLoan(null);
      setPayments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [loanData, paymentData] = await Promise.all([
        getOwnServicingLoan(servicingLoanId),
        getOwnPaymentsForLoan(servicingLoanId)
      ]);

      setLoan(loanData);
      setPayments(paymentData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load loan detail');
    } finally {
      setLoading(false);
    }
  }, [servicingLoanId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const denyMutation = useCallback(async () => ({
    success: false,
    error: 'Loan officers have read-only servicing access.'
  }), []);

  const handleDelete = useCallback(async () => false, []);

  return {
    loan,
    payments,
    form1098Data: null,
    loading,
    error,
    refresh,
    handleRecordPayment: denyMutation,
    handleReversePayment: denyMutation,
    handleDelete
  };
}
